import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import Dropbox from '../icons/dropbox.svg?react';
import GoogleDrive from '../icons/google.svg?react';
import Notion from '../icons/notion.svg?react';
import Globe from '../icons/globe-01.svg?react';
import { SourceType } from '@sc/graphql';
import { Fragment, ReactNode, SVGProps } from 'react';

export type GroundingChunkDisplay = {
  title: string;
  url: string | null;
  hostedIntegrationPath: string | null;
  file: { name: string } | null;
};

export const sourceIconMap: Record<SourceType, HeroIconType> = {
  [SourceType.dropbox]: Dropbox,
  [SourceType.googleDrive]: GoogleDrive,
  [SourceType.notion]: Notion,
  [SourceType.file]: ArrowUpTrayIcon,
  [SourceType.url]: Globe,
};

export const sourceNiceName: Record<SourceType, string> = {
  [SourceType.dropbox]: 'Dropbox',
  [SourceType.googleDrive]: 'Google Drive',
  [SourceType.notion]: 'Notion',
  [SourceType.file]: 'Files',
  [SourceType.url]: 'URL',
};

// Breadcrumb path on one truncating line, the document title on the next, so a
// deep path stays tidy instead of wrapping across several lines.
const integrationDisplayPath = ({
  hostedIntegrationPath,
  title,
}: GroundingChunkDisplay) =>
  !!hostedIntegrationPath && (
    <>
      <div className="truncate">
        {transformIntegrationPath(hostedIntegrationPath)}
      </div>
      <div className="truncate">{title}</div>
    </>
  );

export const sourceDisplayPath: Record<
  SourceType,
  (chunk: GroundingChunkDisplay) => ReactNode
> = {
  [SourceType.dropbox]: integrationDisplayPath,
  [SourceType.googleDrive]: integrationDisplayPath,
  [SourceType.notion]: integrationDisplayPath,
  [SourceType.file]: ({ file }) => !!file?.name && <span>{file.name}</span>,
  [SourceType.url]: ({ url }) => !!url && <span>{url}</span>,
};

export const transformIntegrationPath = (path: string) => {
  const names = path.split('/').map((p) => {
    const [name] = p.split('#');

    return name;
  });

  const segments =
    names.length > 2 ? [names[0], '…', names.at(-1)] : names;

  return segments.map((p, i) => (
    <Fragment key={`${i}-${p}`}>
      <span className="truncate">{p}</span>
      <span>/</span>
    </Fragment>
  ));
};

export const SourceIcon = ({
  type,
  ...rest
}: SVGProps<SVGSVGElement> & { type: SourceType }) => {
  const Icon = sourceIconMap[type];

  return <Icon {...rest} />;
};
